import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  ReactDefaultProps,
  ReactComponentStaticProps,
} from "../../types/react";

type TableContextType = {
  hasColumnHeader: boolean;
  collapseRows: boolean;
  setCollapseRows: React.Dispatch<React.SetStateAction<boolean>>;
  headerColumnIds: string[];
  setHeaderColumnIds: React.Dispatch<React.SetStateAction<string[]>>;
};

export const TableContext = React.createContext<TableContextType | undefined>(
  undefined
);

type TableContextBaseProps = {
  hasColumnHeader?: boolean;
};

export type TableContextProps = React.PropsWithChildren<TableContextBaseProps>;

const defaultProps = {
  hasColumnHeader: false,
} as const;

const validatedDefaultProps: ReactDefaultProps<
  TableContextBaseProps,
  typeof defaultProps
> = defaultProps;

export const TableProvider: ((props: TableContextProps) => JSX.Element) &
  ReactComponentStaticProps<TableContextProps> = ({
  children,
  hasColumnHeader = defaultProps.hasColumnHeader,
}) => {
  const [collapseRows, setCollapseRows] = useState(false);
  const [headerColumnIds, setHeaderColumnIds] = useState<string[]>([]);

  return (
    <TableContext.Provider
      value={{
        hasColumnHeader,
        headerColumnIds,
        setHeaderColumnIds,
        collapseRows,
        setCollapseRows,
      }}
    >
      {children}
    </TableContext.Provider>
  );
};

TableProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  hasColumnHeader: PropTypes.bool,
};

TableProvider.defaultProps = validatedDefaultProps;

export const useTable = (): TableContextType | never => {
  const context = React.useContext(TableContext);

  if (context === undefined) {
    throw new Error("useTable must be used within a TableProvider");
  }

  return context;
};
